import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";

export function useQuickBackup({
  enabled,
  preview,
  createBackup,
  assertSaved,
  onMessage,
}: {
  enabled: boolean;
  preview: boolean;
  createBackup: (password: string, signal: AbortSignal) => Promise<string>;
  assertSaved: () => void;
  onMessage: (message: string) => void;
}) {
  const { t } = useTranslation("quickBackup");
  const { t: brand } = useTranslation("brand");
  const passwordHintId = useId();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Keep the passphrase only for the lifetime of this console, never in browser storage.
  const sessionPassword = useRef("");
  const controller = useRef<AbortController | undefined>(undefined);
  const mounted = useRef(true);
  const trigger = useRef<HTMLButtonElement>(null);
  function close() {
    controller.current?.abort();
    setOpen(false);
    setPassword("");
    setError("");
  }
  const dialogRef = useDialogFocus(open, close, () => trigger.current);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
      sessionPassword.current = "";
    };
  }, []);

  async function download(passphrase: string) {
    if (controller.current || passphrase.length < 12) return;
    const operation = new AbortController();
    controller.current = operation;
    setBusy(true);
    setError("");
    try {
      assertSaved();
      const encrypted = await createBackup(passphrase, operation.signal);
      operation.signal.throwIfAborted();
      const blob = new Blob([encrypted], { type: "application/octet-stream" });
      if (blob.size > 100 * 1024 * 1024) throw new Error(t("tooLarge"));
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${preview ? t("demoFilename") : brand("backupFilenamePrefix")}-${new Date().toISOString().replace(/[:.]/g, "-")}.rollclassbackup`;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      sessionPassword.current = passphrase;
      setOpen(false);
      setPassword("");
      onMessage(preview ? t("demoDownloaded") : t("downloaded"));
    } catch (caught) {
      if (!mounted.current) return;
      const canceled = caught instanceof DOMException && caught.name === "AbortError";
      const message = canceled ? t("canceled") : caught instanceof Error ? caught.message : t("failed");
      if (!canceled) setError(message);
      onMessage(message);
    } finally {
      controller.current = undefined;
      if (mounted.current) setBusy(false);
    }
  }

  function start() {
    if (controller.current) return;
    try {
      assertSaved();
    } catch (caught) {
      onMessage(caught instanceof Error ? caught.message : t("failed"));
      return;
    }
    if (sessionPassword.current) void download(sessionPassword.current);
    else {
      setError("");
      setOpen(true);
    }
  }

  return {
    button: enabled ? (
      <button ref={trigger} type="button" className="topbar-backup" disabled={busy} onClick={start}>
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          width="16"
          height="16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 3v12m-4-4 4 4 4-4M5 15v5h14v-5" />
        </svg>
        {busy ? t("busy") : t("action")}
      </button>
    ) : null,
    dialog: open
      ? createPortal(
          <div className="record-export-backdrop">
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby="quick-backup-title"
              className="paper-card quick-backup-dialog"
              tabIndex={-1}
              aria-busy={busy}
            >
              <h2 id="quick-backup-title">{preview ? t("demoTitle") : t("title")}</h2>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void download(password);
                }}
              >
                <label className="quick-backup-password">
                  {t("password")}
                  <input
                    data-dialog-initial-focus
                    type="password"
                    autoComplete="new-password"
                    minLength={12}
                    required
                    aria-describedby={passwordHintId}
                    disabled={busy}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </label>
                <p id={passwordHintId} className="quick-backup-hint">
                  {t("sessionHint")}
                </p>
                {error && <p role="alert">{error}</p>}
                <div className="quick-backup-actions">
                  <button type="button" onClick={close}>
                    {busy ? t("cancelBackup") : t("cancel")}
                  </button>
                  <button type="submit" className="primary-action" disabled={busy || password.length < 12}>
                    {busy ? t("busy") : t("download")}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body,
        )
      : null,
  };
}
